import * as mutations from "../../graphql/mutations";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { v4 as uuid } from "uuid";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiClient } from "../../helpers/apiClient";
import { useAuthInfo } from "../../helpers/useAuthInfo";
import { packages, CGI_BLACK } from "../../helpers/enums";
import {
  Box,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
} from "@mui/material";

export default function StructuredEntryModal({
  open,
  setOpen,
  moduleId,
  structuredEntry,
  fetchAssessmentData,
  setOpenLoading,
  setNotificationMessage,
  setOpenNotification,
}) {
  const emptyStructuredEntry = {
    id: "",
    title: "",
    description: "",
    source: "",
    threshold: 0,
    entries: [],
  };

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    bgcolor: "background.paper",
    width: 1000,
    borderRadius: 5,
    boxShadow: 24,
    p: 2,
    overflowY: "auto",
    maxHeight: "90vh",
  };

  const { t } = useTranslation();
  const { userGroups } = useAuthInfo();

  const [selectedPackage, setSelectedPackage] = useState(packages.BASIC);
  const [newStructuredEntry, setNewStructuredEntry] =
    useState(emptyStructuredEntry);

  function resetData() {
    setSelectedPackage(packages.BASIC);
    setNewStructuredEntry(emptyStructuredEntry);
  }

  const handleStructuredEntryDataChange = (field, value) => {
    setNewStructuredEntry({ ...newStructuredEntry, [field]: value });
  };

  const handleAddNewEntry = () => {
    setNewStructuredEntry((prev) => ({
      ...prev,
      entries: [
        ...prev.entries,
        {
          id: uuid(), //temporaryId
          impact: "",
          risk: "",
          opportunity: "",
          example: "",
        },
      ],
    }));
  };

  const handleDeleteEntry = (entryId) => {
    setNewStructuredEntry((prev) => ({
      ...prev,
      entries: prev.entries.filter((item) => item.id !== entryId),
    }));
  };

  const handleStructedEntryDetailsDataChange = (entryId, field, value) => {
    setNewStructuredEntry((prev) => ({
      ...prev,
      entries: prev.entries.map((item) =>
        item.id === entryId ? { ...item, [field]: value } : item,
      ),
    }));
  };

  async function createQuestion() {
    setOpen(false);
    setOpenLoading(true);
    try {
      const entries = newStructuredEntry.entries.map((entry) => ({
        impact: entry.impact,
        risk: entry.risk,
        opportunity: entry.opportunity,
        example: entry.example,
      }));

      await apiClient.graphql({
        query: mutations.createStructuredEntry,
        variables: {
          input: {
            title: newStructuredEntry.title,
            description: newStructuredEntry.description,
            source: newStructuredEntry.source,
            threshold: newStructuredEntry.threshold,
            entries: entries,
            moduleID: moduleId,
            ownerGroups: userGroups,
          },
        },
      });

      await fetchAssessmentData();
      setNotificationMessage(`${t("question")} ${t("added")}`);
    } catch (error) {
      console.error(error);
      setNotificationMessage(t("errorMessage"));
    }
    setOpenNotification(true);
    resetData();
    setOpenLoading(false);
  }

  async function changeQuestion() {
    setOpen(false);
    setOpenLoading(true);
    try {
      const entries = newStructuredEntry.entries.map((entry) => ({
        impact: entry.impact,
        risk: entry.risk,
        opportunity: entry.opportunity,
        example: entry.example,
      }));

      await apiClient.graphql({
        query: mutations.updateStructuredEntry,
        variables: {
          input: {
            id: newStructuredEntry.id,
            title: newStructuredEntry.title,
            description: newStructuredEntry.description,
            source: newStructuredEntry.source,
            threshold: newStructuredEntry.threshold,
            entries,
          },
        },
      });

      await fetchAssessmentData();

      setNotificationMessage(`${t("question")} ${t("updated")}`);
    } catch (error) {
      console.error(error);
      setNotificationMessage(t("errorMessage"));
    }
    resetData();
    setOpenNotification(true);
    setOpenLoading(false);
  }

  useEffect(() => {
    if (open) {
      if (structuredEntry.id !== "") {
        setSelectedPackage(structuredEntry.package);
        setNewStructuredEntry(structuredEntry);
      } else {
        resetData();
      }
    } else {
      resetData();
    }
  }, [open]);

  return (
    <Modal open={open} disableAutoFocus={true}>
      <Box sx={style}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography sx={{ fontSize: 17 }}>
              {newStructuredEntry.id !== ""
                ? t("updateQuestion")
                : t("createNewQuestion")}
            </Typography>
            <IconButton onClick={() => setOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider sx={{ my: 1 }} />
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              mt: 3,
            }}
          >
            <TextField
              color="secondary"
              InputLabelProps={{
                shrink: true,
              }}
              label={t("title")}
              variant="outlined"
              size="small"
              sx={{ width: "60%" }}
              value={newStructuredEntry.title || ""}
              onChange={(event) => {
                handleStructuredEntryDataChange("title", event.target.value);
              }}
            />
            <FormControl sx={{ width: "20%", ml: 2 }}>
              <InputLabel>{t("package")}</InputLabel>
              <Select
                InputLabelProps={{
                  shrink: true,
                }}
                label={t("package")}
                variant="outlined"
                size="small"
                value={selectedPackage}
                onChange={(event) => {
                  handleStructuredEntryDataChange(
                    "package",
                    event.target.value,
                  );
                  setSelectedPackage(event.target.value);
                }}
              >
                {Object.values(packages).map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              InputLabelProps={{
                shrink: true,
              }}
              label={t("threshold")}
              type="number"
              variant="outlined"
              size="small"
              sx={{ width: "20%", ml: 2 }}
              value={newStructuredEntry.threshold}
              onChange={(event) => {
                if (event.target.value <= 15 && event.target.value >= 0)
                  handleStructuredEntryDataChange(
                    "threshold",
                    event.target.value,
                  );
              }}
            />
          </Box>
          <TextField
            color="secondary"
            InputLabelProps={{
              shrink: true,
            }}
            label={"Description"}
            variant="outlined"
            size="small"
            multiline
            spellCheck={false}
            rows={4}
            sx={{ width: 1, mt: 3 }}
            value={newStructuredEntry.description || ""}
            onChange={(event) => {
              handleStructuredEntryDataChange(
                "description",
                event.target.value,
              );
            }}
          />
          <TextField
            color="secondary"
            InputLabelProps={{
              shrink: true,
            }}
            label={t("sourceo")}
            variant="outlined"
            size="small"
            multiline
            spellCheck={false}
            rows={1}
            sx={{ width: 1, mt: 3 }}
            value={newStructuredEntry.source || ""}
            onChange={(event) => {
              handleStructuredEntryDataChange("source", event.target.value);
            }}
          />
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 2.5,
            }}
          >
            <Divider
              flexItem
              textAlign="left"
              sx={{
                width: 0.92,
                color: CGI_BLACK,
                userSelect: "none",
                bgcolor: "transparent",
                "&::before, &::after": {
                  borderColor: CGI_BLACK,
                },
              }}
            >
              {t("entryDetails")}
            </Divider>
            <IconButton
              sx={{ ml: 3, border: 1, width: 25, height: 25 }}
              onClick={() => handleAddNewEntry()}
            >
              <AddIcon />
            </IconButton>
          </Box>
          {newStructuredEntry.entries.length === 0 && (
            <Typography
              sx={{
                alignSelf: "center",
                fontFamily: "Inter",
                fontSize: 14,
                mb: 1,
              }}
            >
              {t("noEntryDetails")}
            </Typography>
          )}
          {newStructuredEntry.entries.map((sData, sIndex) => (
            <Box
              key={sIndex}
              sx={{ display: "flex", flexDirection: "row", mt: 2 }}
            >
              <>
                <RequirementInputField
                  ml={0}
                  label={t("impact")}
                  requirement={sData}
                  fieldName={"impact"}
                  updateQuestion={handleStructedEntryDetailsDataChange}
                />
                <RequirementInputField
                  label={t("risk")}
                  requirement={sData}
                  fieldName={"risk"}
                  updateQuestion={handleStructedEntryDetailsDataChange}
                />
                <RequirementInputField
                  label={t("opportunity")}
                  requirement={sData}
                  fieldName={"opportunity"}
                  updateQuestion={handleStructedEntryDetailsDataChange}
                />
                <RequirementInputField
                  label={t("example")}
                  requirement={sData}
                  fieldName={"example"}
                  updateQuestion={handleStructedEntryDetailsDataChange}
                />
              </>
              <IconButton
                sx={{
                  width: 30,
                  height: 30,
                  mx: 1,
                }}
                onClick={() => handleDeleteEntry(sData.id)}
              >
                <DeleteForeverIcon sx={{ width: 20, height: 20 }} />
              </IconButton>
            </Box>
          ))}
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-end",
          }}
        >
          <IconButton
            sx={{ mt: 3 }}
            onClick={() => {
              newStructuredEntry.id !== ""
                ? changeQuestion()
                : createQuestion();
            }}
          >
            <CheckIcon />
          </IconButton>
        </Box>
      </Box>
    </Modal>
  );
}

function RequirementInputField({
  ml = 1,
  label,
  requirement,
  updateQuestion,
  fieldName,
}) {
  return (
    <TextField
      label={label}
      InputLabelProps={{
        shrink: true,
      }}
      variant="outlined"
      size="small"
      sx={{
        width: "25%",
        ml: ml,
        ".MuiInputBase-root": { pb: 0.5, px: 1, pt: 1 },
      }}
      inputProps={{ style: { fontSize: 14, lineHeight: 1 } }}
      multiline
      spellCheck={false}
      rows={4}
      value={requirement[fieldName]}
      onChange={(event) => {
        updateQuestion(requirement.id, fieldName, event.target.value);
      }}
    />
  );
}
